import { useState } from 'react';
import type { Location } from '@salonomy/types';
import { Button, Field, Modal, TextInput } from '@salonomy/ui';
import { useCreateLocation, useUpdateLocation } from '../../data/locations.js';
import { useToast } from '../../lib/toast.js';

/**
 * Create / edit a location (WS6 item 4). A salon has one or more physical sites; staff are assigned
 * to a location from the team roster (StaffModal) or reassigned inline in LocationsPanel. Manager+
 * only — the panel never renders the controls that open this for anyone below manager, but the
 * mutations themselves re-check on the server (POST/PATCH /locations require manager).
 *
 * Address fields are optional: a single-chair home studio may have only a name. Timezone is left to
 * the salon default on create (the server applies it) and is not exposed here — multi-timezone
 * salons are out of scope for this surface; flag for a design round if that ever changes.
 */
export function LocationModal({ existing, onClose }: { existing?: Location; onClose: () => void }) {
  const create = useCreateLocation();
  const update = useUpdateLocation();
  const toast = useToast();
  const editing = Boolean(existing);

  const [name, setName] = useState(existing?.name ?? '');
  const [addressLine1, setAddressLine1] = useState(existing?.addressLine1 ?? '');
  const [addressLine2, setAddressLine2] = useState(existing?.addressLine2 ?? '');
  const [city, setCity] = useState(existing?.city ?? '');
  const [postcode, setPostcode] = useState(existing?.postcode ?? '');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim()) {
      toast.error('A location name is required.');
      return;
    }
    setBusy(true);
    const body = {
      name: name.trim(),
      addressLine1: addressLine1.trim() || null,
      addressLine2: addressLine2.trim() || null,
      city: city.trim() || null,
      postcode: postcode.trim() || null,
    };
    try {
      if (editing && existing) {
        await update.mutateAsync({ id: existing.id, body });
        toast.success('Location updated');
      } else {
        await create.mutateAsync(body);
        toast.success('Location added');
      }
      onClose();
    } catch {
      toast.error('Could not save the location.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit location' : 'Add location'}
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {editing ? 'Save' : 'Add location'}
          </Button>
        </>
      }
    >
      <Field label="Name" hint='What the team calls this site, e.g. "Soho" or "The studio".'>
        <TextInput
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Soho"
        />
      </Field>

      <div className="section-title" style={{ fontSize: 15, margin: '8px 0 2px' }}>
        Address
      </div>
      <Field label="Address line 1" hint="Optional">
        <TextInput
          value={addressLine1}
          onChange={(e) => setAddressLine1(e.target.value)}
          placeholder="12 Carnaby Street"
        />
      </Field>
      <Field label="Address line 2" hint="Optional">
        <TextInput value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
        <Field label="City" hint="Optional">
          <TextInput value={city} onChange={(e) => setCity(e.target.value)} placeholder="London" />
        </Field>
        <Field label="Postcode" hint="Optional">
          <TextInput
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="W1F 9PS"
          />
        </Field>
      </div>
    </Modal>
  );
}
